import React, { useEffect, useState } from 'react';
import format from 'date-fns/format';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { SideNavigator } from '../components/SideNavigator';
import { DynamicForm } from '../../../components/form/dynamicForm';
import { DYNAMIC_FORM_TYPES } from '../../../components/form/dynamicForm/types';
import { FORM_FIELDS } from '../../../components/form/dynamicForm/consts';
import { usePostStartOb } from '../hooks/usePostStartOb';
import { useStore } from '../../../store';
import { VIEWS_MAP } from '../../../router/constants';
import { IFormData } from '../store/createAccountantOnboarding';
import { takeSnapshot } from '../../../services/snapshot.service';
import { OnboardingSteps } from '../consts';
import { eventBus } from '../../../services/eventBus';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { extractErrorFromCode } from '../../../helpers';
import { IServerErrorResponse } from '../../../types';

export const PersonalDetails = () => {
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [formData, setFormData] = useState<IFormData | null>(null);
  const { mutateAsync, isLoading, error } = usePostStartOb();
  const setMfaData = useStore((state) => state.setMfaData);
  const setFormDataInStore = useStore((state) => state.setFormData);
  const stateFormData = useStore((state) => state.formData);
  const obData = useStore((state) => state.obData);
  const setObData = useStore((state) => state.setObData);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string>('');

  const handleSubmit = async () => {
    if (!isFormValid || !formData) {
      eventBus.emit('triggerFormValidation', '');
      return;
    }
    const { firstName, lastName, phone } = formData;
    const res = await mutateAsync({
      customerId: obData.customerId,
      authorizedUserId: obData.authorizedUserId,
      phoneNumber: phone.replace('+1 ', ''),
      lastName,
      firstName,
    });
    const consentFormData = await takeSnapshot('elWrapperSnapShot');

    if (typeof consentFormData === 'object') setObData({ ...obData, consentFormData: consentFormData as FormData });

    setFormDataInStore({ firstName, lastName, phone });
    setMfaData(res);
    navigate(`/${VIEWS_MAP.ACCOUNTANT_ONBOARDING.START}/${VIEWS_MAP.ACCOUNTANT_ONBOARDING.VERIFY_PHONE}`);
  };

  useEffect(() => {
    if (error) {
      const errorContent = extractErrorFromCode(error as IServerErrorResponse);
      setErrMsg(errorContent);
    }
  }, [error]);

  useEffect(() => {
    trackMixPanel('Page view', { pageName: 'Accountant provide details' });
    setObData({ ...obData, lastStepVisit: OnboardingSteps.personalDetails });
  }, []);

  return (
    <div className='flex items-center grow h-full'>
      <SideNavigator />
      <div id='elWrapperSnapShot' className='flex flex-col justify-center grow h-full bg-bw00 pl-[120px]'>
        <div className='flex flex-col items-center justify-center grow max-w-[485px]'>
          <Heading
            title={t('ACCOUNTANT_OB.PERSONAL_DETAILS.HEADING')}
            className='font-metricRMedium text-bw08 text-[28px] mb-[24px]'
          />
          <DynamicForm
            shouldHideFooter
            onFormData={setFormData}
            formValidationStatus={setIsFormValid}
            shouldAvoidValidateAfterPreFill
            preFilledValue={{ inputId: FORM_FIELDS.TIME_STAMP, value: format(new Date(), 'dd MMM yyyy') }}
            preFillData={stateFormData || {}}
            formType={DYNAMIC_FORM_TYPES.ACCOUNTANT}
            triggerFormSubmit={() => {}}
            triggerDelete={() => {}}
          />
          {errMsg && <Heading title={errMsg} className='font-metricR text-red02 text-[12px] mt-[16px] mr-auto' />}
          <div className='flex flex-col w-full mt-[34px] rounded-[12px] bg-bw00 border border-bw03 px-[16px] py-[16px]'>
            <Heading
              title={t('ACCOUNTANT_OB.PERSONAL_DETAILS.DISCLAIMER_HEADING')}
              className='font-metricR text-bw08 text-[16px]'
            />
            <Heading
              title={t('ACCOUNTANT_OB.PERSONAL_DETAILS.DISCLAIMER_CONTENT')}
              className='font-metricR text-[14px] text-bw07'
            />
          </div>
        </div>
        <div className='w-full flex items-center justify-center pb-[40px] max-w-[485px]'>
          <Button
            isLoading={isLoading}
            onClick={handleSubmit}
            className='w-full bg-action03 hover:bg-action04 font-metricRMedium text-bw00 py-[17px] rounded-[4px]'
            title={t('ACCOUNTANT_OB.PERSONAL_DETAILS.CTA')}
            id='accept'
          />
        </div>
      </div>
    </div>
  );
};
